import { z } from 'zod';
import { DASHBOARD_ATTENTION_OUTCOMES, type DashboardAttentionOutcome } from '@helios/types';
import { apiClient, type RequestOptions } from '@/services/apiClient';
import {
  readVerifiedChange,
  readVerifiedChangeSeenReceipt,
  type VerifiedChange,
  type VerifiedChangeScope,
} from '@/components/dashboard/verified-change';

const pageSchema = z
  .object({
    serverTimestamp: z.string().datetime(),
    cursor: z.string().min(1).max(2048).nullable(),
    hasMore: z.boolean(),
    items: z.array(z.unknown()).max(100),
  })
  .strict()
  .refine((page) => page.hasMore === (page.cursor !== null));
export interface VerifiedChangePage {
  serverTimestamp: string;
  cursor: string | null;
  hasMore: boolean;
  items: readonly VerifiedChange[];
}
export interface VerifiedChangeFilter {
  readonly outcomes?: readonly DashboardAttentionOutcome[];
  readonly entityIds?: readonly string[];
}
const filterSchema = z
  .object({
    outcomes: z
      .array(z.enum(DASHBOARD_ATTENTION_OUTCOMES))
      .max(DASHBOARD_ATTENTION_OUTCOMES.length)
      .optional(),
    entityIds: z.array(z.string().uuid()).max(100).optional(),
  })
  .strict();

export const verifiedChangeClient = {
  async list(
    limit: number,
    options: RequestOptions,
    cursor?: string,
    filter: VerifiedChangeFilter = {},
  ): Promise<VerifiedChangePage> {
    if (!options.sessionOrigin)
      throw new Error('Current sign-in is required to read observed changes');
    if (!Number.isInteger(limit) || limit < 1 || limit > 100)
      throw new Error('Invalid observed-change page size');
    if (cursor !== undefined && (!cursor || cursor.length > 2048))
      throw new Error('Invalid observed-change continuation');
    const parsedFilter = filterSchema.safeParse(filter);
    if (!parsedFilter.success) throw new Error('Invalid observed-change source filter');
    if (parsedFilter.data.outcomes?.length === 0 || parsedFilter.data.entityIds?.length === 0)
      throw new Error('An explicit observed source is required');
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set('cursor', cursor);
    if (parsedFilter.data.outcomes)
      params.set('outcomes', [...new Set(parsedFilter.data.outcomes)].sort().join(','));
    if (parsedFilter.data.entityIds)
      params.set('entityIds', [...new Set(parsedFilter.data.entityIds)].sort().join(','));
    const result = await apiClient.get<unknown>(
      `/realtime/dashboard-attention?${params.toString()}`,
      options,
    );
    if (!result.ok) throw new Error(result.error ?? 'Observed changes are unavailable');
    const page = pageSchema.safeParse(result.data);
    if (!page.success) throw new Error('Observed change evidence is unavailable');
    const items = page.data.items.map(readVerifiedChange);
    if (
      items.some((item) => item === null) ||
      items.some(
        (item) =>
          item &&
          ((parsedFilter.data.outcomes && !parsedFilter.data.outcomes.includes(item.outcome)) ||
            (parsedFilter.data.entityIds && !parsedFilter.data.entityIds.includes(item.entityId))),
      ) ||
      new Set(items.map((item) => item?.id)).size !== items.length
    ) {
      throw new Error('Observed change evidence is invalid');
    }
    return { ...page.data, items: items as VerifiedChange[] };
  },

  async seen(change: VerifiedChange, scope: VerifiedChangeScope, options: RequestOptions) {
    if (!options.sessionOrigin || options.sessionOrigin !== scope.sessionOrigin)
      throw new Error('Current sign-in is required to record seen evidence');
    const result = await apiClient.post<unknown>(
      `/realtime/dashboard-attention/${change.id}/seen`,
      {
        expectedEntityVersion: change.entityVersion,
        expectedEvidenceFingerprintSha256: change.evidenceFingerprintSha256,
      },
      { ...options, allowRefresh: false },
    );
    if (!result.ok) throw new Error(result.error ?? 'Seen receipt was not recorded');
    const receipt = readVerifiedChangeSeenReceipt(result.data, scope, change);
    if (!receipt) throw new Error('Seen receipt does not match the observed version');
    return receipt;
  },
};
